import type {
  CertificationEntry,
  ContactField,
  EducationEntry,
  ExperienceEntry,
  Period,
  ProjectEntry,
  SkillEntry,
  ContactLink,
} from './types';

export const MAX_SHORT = 120;
export const MAX_BULLET = 400;
export const MAX_BULLETS = 12;
export const MAX_SUMMARY = 1_200;

const MONTHS: Record<string, string> = {
  jan: '01', january: '01',
  feb: '02', february: '02',
  mar: '03', march: '03',
  apr: '04', april: '04',
  may: '05',
  jun: '06', june: '06',
  jul: '07', july: '07',
  aug: '08', august: '08',
  sep: '09', sept: '09', september: '09',
  oct: '10', october: '10',
  nov: '11', november: '11',
  dec: '12', december: '12',
};

const PRESENT_WORDS = new Set(['present', 'current', 'now', 'today']);

const EMAIL_RE = /^[^\s@]+@[^\s@.,;]+(\.[^\s@.,;]+)+$/;
const URL_RE = /^(https?:\/\/)?[^\s@]+\.[^\s@]{2,}([/?#]\S*)?$/i;

export type DateToken =
  | { ok: true; value: string; current: boolean }
  | { ok: false; error: string };

/**
 * Reads only dates the source states outright: `2021`, `2021-03`, `03/2021`,
 * `Mar 2021`, or a present-tense word. Anything else is reported as unreadable
 * instead of being guessed, because a wrong date on a resume is a real harm.
 */
export function parseDateToken(raw: string): DateToken {
  const text = raw.trim().toLowerCase().replace(/[’']/g, '');
  if (text.length === 0) return { ok: true, value: '', current: false };
  if (PRESENT_WORDS.has(text)) return { ok: true, value: '', current: true };

  if (/^(19|20)\d{2}$/.test(text)) return { ok: true, value: text, current: false };

  const yearFirst = text.match(/^(19|20)\d{2}[-/.]\s*(0?[1-9]|1[0-2])$/);
  if (yearFirst?.[1] && yearFirst[2]) {
    return { ok: true, value: `${yearFirst[1]}-${pad(yearFirst[2])}`, current: false };
  }

  const monthFirst = text.match(/^(0?[1-9]|1[0-2])[-/.]\s*(19|20)\d{2}$/);
  if (monthFirst?.[1] && monthFirst[2]) {
    return { ok: true, value: `${monthFirst[2]}-${pad(monthFirst[1])}`, current: false };
  }

  const named = text.match(/^([a-z]+)\.?\s+(19|20)\d{2}$/);
  if (named?.[1] && named[2]) {
    const month = MONTHS[named[1]];
    if (month) return { ok: true, value: `${named[2]}-${month}`, current: false };
  }

  return {
    ok: false,
    error: `"${raw.trim()}" is not a date NextRole can read. Use 2021, 2021-03, 03/2021, Mar 2021, or "present".`,
  };
}

function pad(month: string): string {
  return month.length === 1 ? `0${month}` : month;
}

/** Compares normalized dates at the precision the source actually provided. */
function isBefore(start: string, end: string): boolean {
  return end < start;
}

export type FieldIssues = {
  errors: Record<string, string>;
  warnings: Record<string, string>;
};

function issues(): FieldIssues {
  return { errors: {}, warnings: {} };
}

export function validatePeriod(
  period: Period,
  result: FieldIssues,
  key: string,
  today: Date,
): void {
  let startValue = '';
  let endValue = '';

  const start = parseDateToken(period.start);
  if (!start.ok) result.errors[`${key}.start`] = start.error;
  else startValue = start.value;

  const end = parseDateToken(period.end);
  if (!end.ok) result.errors[`${key}.end`] = end.error;
  else endValue = end.value;

  if (period.current && period.end.trim().length > 0) {
    result.errors[`${key}.end`] =
      'Choose either "present" or an end date, not both.';
  }

  if (period.current) endValue = String(today.getFullYear());

  if (startValue.length > 0 && endValue.length > 0 && isBefore(startValue, endValue)) {
    result.errors[`${key}.end`] = `${period.end.trim()} ends before ${period.start.trim()} starts. Swap them or correct one.`;
  }

  const thisYear = today.getFullYear();
  for (const [field, value] of [
    [`${key}.start`, startValue],
    [`${key}.end`, endValue],
  ] as const) {
    if (value.length < 4) continue;
    const year = Number(value.slice(0, 4));
    if (year > thisYear) {
      result.errors[field] = `That date is in the future (${year}). A profile records facts that already happened.`;
    }
  }
}

function checkLength(label: string, value: string, max: number, result: FieldIssues, key: string): void {
  if (value.length > max) {
    result.errors[key] = `${label} is ${value.length} characters — keep it under ${max}.`;
  }
}

function checkBullets(label: string, bullets: string[], result: FieldIssues, key: string): void {
  if (bullets.length > MAX_BULLETS) {
    result.errors[key] = `${label} has ${bullets.length} lines — keep it to ${MAX_BULLETS}.`;
    return;
  }
  bullets.forEach((bullet, index) => {
    if (bullet.length > MAX_BULLET) {
      result.errors[key] = `${label} line ${index + 1} is over ${MAX_BULLET} characters. Shorten it so it stays readable.`;
    }
  });
}

export function validateExperience(
  entry: ExperienceEntry,
  today: Date = new Date(),
): FieldIssues {
  const result = issues();
  if (entry.organization.trim().length === 0 && entry.role.trim().length === 0) {
    result.errors.identity =
      'Add the employer, the role, or both. An empty entry cannot be confirmed.';
  }
  checkLength('Employer', entry.organization, MAX_SHORT, result, 'organization');
  checkLength('Role', entry.role, MAX_SHORT, result, 'role');
  checkLength('Location', entry.location, MAX_SHORT, result, 'location');
  checkBullets('Responsibilities', entry.responsibilities, result, 'responsibilities');
  checkBullets('Achievements', entry.achievements, result, 'achievements');
  validatePeriod(entry.period, result, 'period', today);
  return result;
}

export function validateEducation(
  entry: EducationEntry,
  today: Date = new Date(),
): FieldIssues {
  const result = issues();
  if (entry.institution.trim().length === 0 && entry.credential.trim().length === 0) {
    result.errors.identity =
      'Add the school or the qualification. An empty entry cannot be confirmed.';
  }
  checkLength('Institution', entry.institution, MAX_SHORT, result, 'institution');
  checkLength('Credential', entry.credential, MAX_SHORT, result, 'credential');
  checkLength('Field of study', entry.area, MAX_SHORT, result, 'area');
  validatePeriod(entry.period, result, 'period', today);
  return result;
}

export function validateSkill(entry: SkillEntry): FieldIssues {
  const result = issues();
  const name = entry.name.trim();
  if (name.length === 0) {
    result.errors.name = 'Skill is required. Add the skill name or remove the entry.';
    return result;
  }
  checkLength('Skill', name, MAX_SHORT, result, 'name');
  if (/[,;]/.test(name)) {
    result.warnings.name =
      'This looks like several skills in one line. Add them separately so they can be matched individually.';
  }
  return result;
}

export function validateCertification(
  entry: CertificationEntry,
  today: Date = new Date(),
): FieldIssues {
  const result = issues();
  if (entry.name.trim().length === 0) {
    result.errors.name = 'Certification name is required.';
  }
  checkLength('Certification', entry.name, MAX_SHORT, result, 'name');
  checkLength('Issuer', entry.issuer, MAX_SHORT, result, 'issuer');
  if (entry.date.trim().length > 0) {
    const single = issues();
    validatePeriod(
      { start: '', end: entry.date, current: false, raw: entry.date },
      single,
      'period',
      today,
    );
    const error = single.errors['period.end'];
    if (error) result.errors.date = error;
  }
  return result;
}

export function validateProject(
  entry: ProjectEntry,
  today: Date = new Date(),
): FieldIssues {
  const result = issues();
  if (entry.name.trim().length === 0) {
    result.errors.name = 'Project name is required.';
  }
  checkLength('Project', entry.name, MAX_SHORT, result, 'name');
  checkLength('Description', entry.description, 600, result, 'description');
  if (entry.url.trim().length > 0 && !URL_RE.test(entry.url.trim())) {
    result.errors.url = `"${entry.url.trim()}" does not look like a web address.`;
  }
  validatePeriod(entry.period, result, 'period', today);
  return result;
}

export function validateContactField(
  field: ContactField,
  value: string,
): FieldIssues {
  const result = issues();
  const text = value.trim();
  if (text.length === 0) return result;

  checkLength('This field', text, MAX_SHORT, result, field);

  if (field === 'email' && !EMAIL_RE.test(text)) {
    result.errors.email = `"${text}" is not a valid email address. Check for a missing @ or domain.`;
  }
  if (field === 'phone') {
    const digits = text.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) {
      result.errors.phone = `"${text}" has ${digits.length} digits. A phone number needs 7 to 15.`;
    }
  }
  return result;
}

export function validateContactLink(link: ContactLink): FieldIssues {
  const result = issues();
  if (link.url.trim().length === 0 && link.label.trim().length === 0) return result;
  if (link.url.trim().length > 0 && !URL_RE.test(link.url.trim())) {
    result.errors.url = `"${link.url.trim()}" does not look like a web address.`;
  }
  checkLength('Label', link.label, MAX_SHORT, result, 'label');
  return result;
}

export function validateSummary(value: string): FieldIssues {
  const result = issues();
  if (value.trim().length > MAX_SUMMARY) {
    result.errors.summary = `The summary is ${value.trim().length} characters — keep it under ${MAX_SUMMARY} so it fits on a resume.`;
  }
  return result;
}

export function hasIssues(result: FieldIssues): boolean {
  return Object.keys(result.errors).length > 0;
}

export function firstIssue(result: FieldIssues): string | null {
  return Object.values(result.errors)[0] ?? null;
}
